import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, token, setUser } = useAuth();
  const { t, language } = useLanguage();
  const isWellness = user?.program_type === "wellness";

  // Wellness users start at step 0 (consent). Clinical users start at step 1.
  const [step, setStep] = useState(isWellness ? 0 : 1);
  const [loading, setLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [challengeAlias, setChallengeAlias] = useState("");
  const [data, setData] = useState({
    diabetes_type: "",
    diagnosis_year: "",
    checks_glucose: null,
    cuisines: [],
  });

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1949 }, (_, i) => currentYear - i);

  const cuisineOptions = [
    { value: "african", label: t('cuisines.african') },
    { value: "european", label: t('cuisines.european') },
    { value: "asian", label: t('cuisines.asian') },
    { value: "north_american", label: t('cuisines.north_american') },
    { value: "south_american", label: t('cuisines.south_american') },
    { value: "middle_eastern", label: t('cuisines.middle_eastern') },
    { value: "caribbean", label: t('cuisines.caribbean') },
    { value: "mixed", label: t('cuisines.mixed') },
  ];

  const toggleCuisine = (value) => {
    setData((prev) => ({
      ...prev,
      cuisines: prev.cuisines.includes(value)
        ? prev.cuisines.filter((c) => c !== value)
        : [...prev.cuisines, value],
    }));
  };

  // For wellness users who choose "none" for diabetes, we skip diabetes-specific steps
  const isWellnessNonDiabetic = isWellness && (data.diabetes_type === "none" || data.diabetes_type === "monitoring");

  // Check if diagnosis year should be asked
  const shouldAskDiagnosisYear = ["type1", "type2", "gestational"].includes(data.diabetes_type);

  // Determine the flow steps for wellness users:
  // Step 0: Consent + Alias
  // Step 1: Diabetes type (includes "I don't have diabetes" option for wellness)
  // Step 2: Diagnosis year (only for type1/type2/gestational)
  // Step 3: Glucose monitoring (skip for wellness non-diabetics)
  // Step 4: Cuisines
  const getVisibleSteps = () => {
    if (isWellness) {
      const steps = [0, 1]; // consent, diabetes type
      if (shouldAskDiagnosisYear) steps.push(2);
      if (!isWellnessNonDiabetic) steps.push(3);
      steps.push(4); // cuisines always
      return steps;
    }
    // Clinical flow
    const steps = [1];
    if (shouldAskDiagnosisYear) steps.push(2);
    steps.push(3, 4);
    return steps;
  };

  const visibleSteps = getVisibleSteps();
  const lastStep = visibleSteps[visibleSteps.length - 1];
  const currentStepIndex = visibleSteps.indexOf(step);

  const handleNext = () => {
    // Validate current step
    if (step === 0) {
      if (!consentAccepted) {
        toast.error("Please accept the consent to continue");
        return;
      }
    }
    if (step === 1 && !data.diabetes_type) {
      toast.error(t('onboarding.select_option'));
      return;
    }
    if (step === 2 && shouldAskDiagnosisYear && !data.diagnosis_year) {
      toast.error("Please select when you were diagnosed");
      return;
    }
    if (step === 3 && data.checks_glucose === null) {
      toast.error(t('onboarding.select_option'));
      return;
    }
    if (step === 4 && data.cuisines.length === 0) {
      toast.error(t('onboarding.select_cuisine'));
      return;
    }

    if (step === lastStep) {
      handleSubmit();
      return;
    }

    // Move to next visible step
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setStep(visibleSteps[nextIndex]);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(visibleSteps[prevIndex]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const body = {
        preferred_language: language,
        diet_region: data.cuisines.join(","),
      };

      // For wellness non-diabetics, send "none" and skip glucose
      if (isWellnessNonDiabetic) {
        body.diabetes_type = data.diabetes_type || "none";
        body.checks_glucose = false;
      } else {
        body.diabetes_type = data.diabetes_type;
        body.diagnosis_year = parseInt(data.diagnosis_year) || null;
        body.checks_glucose = data.checks_glucose;
      }

      if (isWellness) {
        body.consent_accepted = consentAccepted;
        body.challenge_alias = challengeAlias.trim() || undefined;
      }

      const response = await fetch(`${API}/onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setUser((prev) => ({
          ...prev,
          onboarding_completed: true,
          challenge_alias: challengeAlias.trim() || prev?.challenge_alias,
          consent_accepted: consentAccepted,
        }));
        toast.success("Profile setup complete!");
        navigate("/welcome");
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to save preferences. Please try again.");
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Failed to save preferences");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg rounded-3xl border border-orange-100/50 shadow-soft-lift">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center gap-2 mb-4">
            {visibleSteps.map((s, idx) => {
              const isCurrent = step === s;
              const isPast = currentStepIndex > idx;
              return (
                <div
                  key={s}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    isCurrent ? "bg-primary" : isPast ? "bg-primary/50" : "bg-orange-100"
                  }`}
                />
              );
            })}
          </div>
          <CardTitle className="font-heading text-2xl font-bold text-secondary">
            {step === 0 && "Let's Get You Set Up!"}
            {step === 1 && (isWellness ? "About Your Health" : t('onboarding.step1_title'))}
            {step === 2 && shouldAskDiagnosisYear && "When were you diagnosed?"}
            {step === 3 && t('onboarding.step2_title')}
            {step === 4 && t('onboarding.step3_title')}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {step === 0 && "Choose your display name and agree to participate"}
            {step === 1 && (isWellness ? "This helps us personalize your experience" : t('onboarding.step1_subtitle'))}
            {step === 2 && shouldAskDiagnosisYear && "This helps us understand your journey better"}
            {step === 3 && t('onboarding.step2_subtitle')}
            {step === 4 && t('onboarding.step3_subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          {/* Step 0: Wellness Consent + Alias */}
          {step === 0 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-secondary">What name would you like to go by?</Label>
                <Input
                  value={challengeAlias}
                  onChange={(e) => setChallengeAlias(e.target.value)}
                  placeholder="e.g. your first name, a nickname, or a fun alias..."
                  className="rounded-2xl border-2 border-orange-100 focus:border-primary h-12"
                  data-testid="challenge-alias-input"
                />
                <p className="text-xs text-muted-foreground">
                  This is the name shown on the leaderboard. You can use an anonymous name if you'd prefer to keep your identity private.
                </p>
              </div>

              <div
                className={`flex items-start space-x-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                  consentAccepted ? "border-primary bg-primary/5" : "border-orange-100"
                }`}
                onClick={() => setConsentAccepted(!consentAccepted)}
                data-testid="consent-checkbox-container"
              >
                <Checkbox
                  checked={consentAccepted}
                  onCheckedChange={setConsentAccepted}
                  id="consent"
                  className="mt-0.5"
                  data-testid="consent-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="consent" className="cursor-pointer text-secondary font-medium text-sm leading-relaxed">
                    I agree to participate in this wellness challenge
                  </Label>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Your employer will only see your challenge alias and aggregated statistics. Your health data, real name, and individual meal details are never shared with your organization.
                  </p>
                </div>
                {consentAccepted && <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />}
              </div>
            </div>
          )}

          {/* Step 1: Diabetes Type */}
          {step === 1 && (
            <RadioGroup
              value={data.diabetes_type}
              onValueChange={(value) => setData({ ...data, diabetes_type: value, diagnosis_year: "" })}
              className="space-y-3"
            >
              {[
                { value: "type1", label: t('onboarding.diabetes_type1') },
                { value: "type2", label: t('onboarding.diabetes_type2') },
                { value: "gestational", label: "Gestational Diabetes" },
                { value: "prediabetes", label: t('onboarding.diabetes_prediabetes') },
                { value: "monitoring", label: t('onboarding.diabetes_monitoring') },
                ...(isWellness ? [{ value: "none", label: "I don't have diabetes" }] : []),
              ].map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center space-x-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                    data.diabetes_type === option.value
                      ? "border-primary bg-primary/5"
                      : "border-orange-100 hover:border-primary/30"
                  }`}
                  onClick={() => setData({ ...data, diabetes_type: option.value, diagnosis_year: "" })}
                >
                  <RadioGroupItem value={option.value} id={option.value} data-testid={`diabetes-${option.value}`} />
                  <Label htmlFor={option.value} className="flex-1 cursor-pointer text-secondary font-medium">
                    {option.label}
                  </Label>
                  {data.diabetes_type === option.value && <Check className="w-5 h-5 text-primary" />}
                </div>
              ))}
            </RadioGroup>
          )}

          {/* Step 2: Diagnosis Year (only for type1, type2, gestational) */}
          {step === 2 && shouldAskDiagnosisYear && (
            <div className="space-y-4">
              <select
                value={data.diagnosis_year}
                onChange={(e) => setData({ ...data, diagnosis_year: e.target.value })}
                className="w-full p-4 rounded-2xl border-2 border-orange-100 bg-background text-secondary font-medium focus:border-primary focus:outline-none"
                data-testid="diagnosis-year-select"
              >
                <option value="">Select year of diagnosis</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <p className="text-sm text-muted-foreground text-center">
                This helps your healthcare provider understand your diabetes journey
              </p>
            </div>
          )}

          {/* Step 3: Glucose Monitoring */}
          {step === 3 && (
            <RadioGroup
              value={data.checks_glucose === null ? "" : data.checks_glucose ? "yes" : "no"}
              onValueChange={(value) => setData({ ...data, checks_glucose: value === "yes" })}
              className="space-y-3"
            >
              {[
                { value: "yes", label: t('onboarding.glucose_yes') },
                { value: "no", label: t('onboarding.glucose_no') },
              ].map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center space-x-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                    (data.checks_glucose === true && option.value === "yes") ||
                    (data.checks_glucose === false && option.value === "no")
                      ? "border-primary bg-primary/5"
                      : "border-orange-100 hover:border-primary/30"
                  }`}
                  onClick={() => setData({ ...data, checks_glucose: option.value === "yes" })}
                >
                  <RadioGroupItem value={option.value} id={`glucose-${option.value}`} data-testid={`glucose-${option.value}`} />
                  <Label htmlFor={`glucose-${option.value}`} className="flex-1 cursor-pointer text-secondary font-medium">
                    {option.label}
                  </Label>
                  {((data.checks_glucose === true && option.value === "yes") ||
                    (data.checks_glucose === false && option.value === "no")) && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </div>
              ))}
            </RadioGroup>
          )}

          {/* Step 4: Cuisines Multi-Select */}
          {step === 4 && (
            <div className="space-y-3">
              {cuisineOptions.map((option) => {
                const isSelected = data.cuisines.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-orange-100 hover:border-primary/30"
                    }`}
                    onClick={() => toggleCuisine(option.value)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleCuisine(option.value)}
                      id={`cuisine-${option.value}`}
                      data-testid={`cuisine-${option.value}`}
                    />
                    <Label
                      htmlFor={`cuisine-${option.value}`}
                      className="flex-1 cursor-pointer text-secondary font-medium"
                    >
                      {option.label}
                    </Label>
                    {isSelected && <Check className="w-5 h-5 text-primary" />}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            {currentStepIndex > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="flex-1 rounded-full border-2 border-border py-6"
              >
                {t('common.back')}
              </Button>
            )}
            <Button
              type="button"
              data-testid="onboarding-next-btn"
              onClick={handleNext}
              disabled={loading}
              className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-button-primary hover:shadow-button-primary-hover transition-all"
            >
              {loading ? t('common.loading') : step === lastStep ? t('onboarding.complete_setup') : t('common.continue')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;
